import DOMPurify from "dompurify";

/**
 * Sanitize an HTML string using DOMPurify.
 * Removes all dangerous tags/attributes (scripts, event handlers, etc.)
 * while preserving safe formatting markup.
 *
 * Usage in JS/computed:
 *   import { sanitize } from "@/helpers/sanitize.helper.js";
 *   sanitize("<p>Hello</p><script>alert(1)</script>")
 *   // → "<p>Hello</p>"
 *
 * Usage in templates (via mixin):
 *   {{ sanitize(apiData) }}
 *   or use v-safe-html="apiData" directive instead of v-html
 *
 * @param {string} dirty - The untrusted HTML string.
 * @param {Object} [config] - Optional DOMPurify config override.
 * @returns {string} Sanitized HTML string.
 */
export const sanitize = (dirty, config) => {
  if (!dirty) return "";
  return DOMPurify.sanitize(String(dirty), config);
};

/**
 * Sanitize but strip ALL HTML — returns plain text only.
 * Useful for inserting user-supplied values into HTML templates safely.
 *
 * @param {string} dirty - The untrusted string.
 * @returns {string} Plain text with all HTML removed.
 */
export const sanitizeToText = (dirty) => {
  if (!dirty) return "";
  return DOMPurify.sanitize(String(dirty), { ALLOWED_TAGS: [] });
};

/**
 * Vue directive: v-safe-html
 * Drop-in replacement for v-html that sanitizes automatically.
 *
 * Usage:
 *   <div v-safe-html="untrustedHtml"></div>
 *
 * Register in main.js:
 *   import { safeHtmlDirective } from "@/helpers/sanitize.helper.js";
 *   app.directive("safe-html", safeHtmlDirective);
 */
export const safeHtmlDirective = {
  mounted(el, binding) {
    el.innerHTML = sanitize(binding.value);
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      el.innerHTML = sanitize(binding.value);
    }
  }
};

export default { sanitize, sanitizeToText, safeHtmlDirective };
